/**
 * MobileBanner
 * Non-blocking warm-amber advisory rendered at the top of the viewport on
 * mobile user agents. Dismissible for the session via the ✕ button.
 *
 * Layout integration: when the banner is present, it adds a
 * `has-mobile-banner` class to <body>. Header.css and global.css key off
 * that class to shift the fixed app header down by the banner's 40px height
 * and enlarge the main content area's top padding to 100px
 * (40 banner + 60 header). This keeps the "is the banner present?" gate
 * identical in JS and CSS — no viewport-breakpoint vs. UA-regex drift.
 *
 * Dismissal is component-local (resets on reload). If persistence is ever
 * wanted, layer localStorage on top of `dismissed` here.
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import './MobileBanner.css';

const BODY_CLASS = 'has-mobile-banner';

const isMobileUserAgent = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const MobileBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const shouldShow = !dismissed && isMobileUserAgent();

  // Sync the `has-mobile-banner` class on <body> with our visibility state.
  // Handles mount (mobile UA), dismiss click, and unmount (desktop UA / route
  // changes) uniformly — when `shouldShow` flips false, the cleanup removes
  // the class regardless of which path got us here.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!shouldShow) return undefined;
    document.body.classList.add(BODY_CLASS);
    return () => {
      document.body.classList.remove(BODY_CLASS);
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  return (
    <div className="mobile-banner" role="status" aria-live="polite">
      <span className="mobile-banner-text">
        Desktop recommended for now.
      </span>
      <button
        type="button"
        className="mobile-banner-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss mobile advisory"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
};

export default MobileBanner;
